import { useCallback, useEffect, useRef, useState } from "react";

const LS_KEY = "pokus_rider_alarm_enabled";

// Synthesized two-tone siren via WebAudio. No asset file, works offline, and
// we can keep looping it at full page volume until the rider acks — which is
// the "loud notification" behavior delivery apps rely on. This only plays when
// the rider dashboard is actually open; OS-level push sounds are controlled by
// the device and we cannot override them from a service worker.
export const useRiderAlarm = () => {
  const [enabled, setEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(LS_KEY);
    return stored === null ? true : stored === "1";
  });
  const [playing, setPlaying] = useState(false);

  const ctxRef = useRef(null);
  const nodesRef = useRef(null);
  const unlockedRef = useRef(false);

  // Browsers block AudioContext until a user gesture — unlock on the first
  // click/touch anywhere in the app so our siren can start from a push later.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const unlock = () => {
      if (unlockedRef.current) return;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        // Resume + play a 1-sample silent buffer to fully unlock iOS Safari.
        const buf = ctx.createBuffer(1, 1, 22050);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        src.start(0);
        if (ctx.state === "suspended") ctx.resume().catch(() => undefined);
        ctxRef.current = ctx;
        unlockedRef.current = true;
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener("pointerdown", unlock, { once: true, passive: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const stop = useCallback(() => {
    const nodes = nodesRef.current;
    if (!nodes) {
      setPlaying(false);
      return;
    }
    try {
      const now = nodes.ctx.currentTime;
      // Quick fade-out to avoid a click on stop.
      nodes.gain.gain.cancelScheduledValues(now);
      nodes.gain.gain.setValueAtTime(nodes.gain.gain.value, now);
      nodes.gain.gain.linearRampToValueAtTime(0.0001, now + 0.06);
      setTimeout(() => {
        try { nodes.osc.stop(); } catch (e) {}
        try { nodes.osc.disconnect(); } catch (e) {}
        try { nodes.gain.disconnect(); } catch (e) {}
      }, 80);
    } catch (e) {}
    nodesRef.current = null;
    setPlaying(false);
  }, []);

  const play = useCallback(
    ({ durationMs = 8000 } = {}) => {
      if (!enabled) return;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;

      // Reuse the unlocked context when possible. If it's still suspended
      // (e.g. no prior user gesture), resume() may reject silently — that's
      // fine, we just don't play.
      let ctx = ctxRef.current;
      if (!ctx) {
        try {
          ctx = new Ctx();
          ctxRef.current = ctx;
        } catch (e) {
          return;
        }
      }
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => undefined);
      }

      // Stop any currently-playing siren before restarting.
      if (nodesRef.current) stop();

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.connect(gain);
      gain.connect(ctx.destination);

      const now = ctx.currentTime;
      // Two-tone emergency-vehicle sweep, repeats every 0.8s.
      const period = 0.8;
      const endAt = now + durationMs / 1000;
      for (let t = now; t < endAt; t += period) {
        osc.frequency.setValueAtTime(880, t);
        osc.frequency.setValueAtTime(660, t + period / 2);
      }
      // Gentle attack, loud sustain, fade at the end.
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.6, now + 0.05);
      gain.gain.setValueAtTime(0.6, endAt - 0.12);
      gain.gain.exponentialRampToValueAtTime(0.0001, endAt);

      osc.start(now);
      osc.stop(endAt + 0.02);
      osc.onended = () => {
        if (nodesRef.current && nodesRef.current.osc === osc) {
          nodesRef.current = null;
          setPlaying(false);
        }
      };
      nodesRef.current = { ctx, osc, gain };
      setPlaying(true);
    },
    [enabled, stop]
  );

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(LS_KEY, next ? "1" : "0"); } catch (e) {}
      if (!next) {
        // Turning off — kill any currently-playing siren.
        const nodes = nodesRef.current;
        if (nodes) {
          try { nodes.osc.stop(); } catch (e) {}
          nodesRef.current = null;
        }
        setPlaying(false);
      }
      return next;
    });
  }, []);

  // Also stop when the tab is hidden — no reason to keep blaring into the void.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden && nodesRef.current) stop();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [stop]);

  return { enabled, playing, play, stop, toggle };
};
